// SteelOps Bids — list, edit, line-item entry, send, PDF, public-link copy.

import { useState, useMemo } from 'react';
import { useApp } from '@/context/AppContext';

const ASSEMBLY_TYPES = ['beam', 'plate', 'tube', 'stair', 'railing', 'decking', 'misc'];
const UNITS          = ['ea', 'ton', 'lf', 'ls', 'sf'];

const SITE_ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const LABOR_RATE_PER_HR = 85;  // shop default; could become a company setting

function lineItemTotal(li) {
  const labor    = (Number(li.laborHrsUnit) || 0) * LABOR_RATE_PER_HR * (Number(li.qty) || 0);
  const material = (Number(li.materialCostUnit) || 0) * (Number(li.qty) || 0);
  const sub      = labor + material;
  return sub * (1 + (Number(li.markup) || 0) / 100);
}

function fmtMoney(n) {
  return `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function AddBidModal({ onClose }) {
  const { addBid, customers, userEmail } = useApp();
  const [form, setForm] = useState({
    bidNumber:   `B-${new Date().getFullYear()}-${String(Date.now()).slice(-3)}`,
    customerId:  '',
    projectName: '',
    bidDate:     new Date().toISOString().slice(0, 10),
    validUntil:  new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    estimator:   (userEmail || '').split('@')[0],
    notes:       '',
  });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.projectName.trim()) return;
    addBid({ ...form, customerId: form.customerId ? Number(form.customerId) : null });
    onClose();
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-title">New Bid</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="field"><label>Bid Number *</label><input value={form.bidNumber} onChange={e => set('bidNumber', e.target.value)} required /></div>
              <div className="field">
                <label>Customer / GC</label>
                <select value={form.customerId} onChange={e => set('customerId', e.target.value)}>
                  <option value="">Select customer…</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="field"><label>Project Name *</label><input value={form.projectName} onChange={e => set('projectName', e.target.value)} placeholder="e.g. Warehouse Structural Steel Package" required /></div>
            <div className="form-row">
              <div className="field"><label>Bid Date</label><input type="date" value={form.bidDate} onChange={e => set('bidDate', e.target.value)} /></div>
              <div className="field"><label>Valid Until</label><input type="date" value={form.validUntil} onChange={e => set('validUntil', e.target.value)} /></div>
            </div>
            <div className="field"><label>Estimator</label><input value={form.estimator} onChange={e => set('estimator', e.target.value)} /></div>
            <div className="field"><label>Notes</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Scope notes, exclusions…" /></div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">Create Bid</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BidLineItemRow({ item, onChange, onDelete }) {
  return (
    <tr>
      <td>
        <select
          value={item.assemblyType}
          onChange={e => onChange({ assemblyType: e.target.value })}
          style={{ background: 'var(--s2)', border: '1px solid var(--b2)', borderRadius: 6, padding: '4px 6px', fontSize: 12, color: 'var(--t1)', width: '100%' }}
        >
          {ASSEMBLY_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
      </td>
      <td>
        <input
          value={item.description}
          onChange={e => onChange({ description: e.target.value })}
          placeholder="Embed plates, W8x31 columns, etc."
          style={{ width: '100%', background: 'var(--s2)', border: '1px solid var(--b2)', borderRadius: 6, padding: '4px 6px', fontSize: 12, color: 'var(--t1)' }}
        />
      </td>
      <td style={{ width: 70 }}>
        <select
          value={item.unit}
          onChange={e => onChange({ unit: e.target.value })}
          style={{ background: 'var(--s2)', border: '1px solid var(--b2)', borderRadius: 6, padding: '4px 6px', fontSize: 12, color: 'var(--t1)', width: '100%' }}
        >
          {UNITS.map(u => <option key={u}>{u}</option>)}
        </select>
      </td>
      <td style={{ width: 70 }}>
        <input type="number" min="0" step="0.1" value={item.qty} onChange={e => onChange({ qty: e.target.value })} style={inputStyle} />
      </td>
      <td style={{ width: 80 }}>
        <input type="number" min="0" step="0.05" value={item.laborHrsUnit} onChange={e => onChange({ laborHrsUnit: e.target.value })} style={inputStyle} title="Labor hours per unit" />
      </td>
      <td style={{ width: 90 }}>
        <input type="number" min="0" step="0.01" value={item.materialCostUnit} onChange={e => onChange({ materialCostUnit: e.target.value })} style={inputStyle} title="Material cost per unit ($)" />
      </td>
      <td style={{ width: 60 }}>
        <input type="number" min="0" step="1" value={item.markup} onChange={e => onChange({ markup: e.target.value })} style={inputStyle} title="Markup %" />
      </td>
      <td style={{ width: 90, textAlign: 'right' }}>
        <span className="mono" style={{ fontWeight: 700 }}>{fmtMoney(lineItemTotal(item))}</span>
      </td>
      <td style={{ width: 30 }}>
        <button className="btn btn-ghost btn-sm" onClick={onDelete} style={{ color: 'var(--rose)' }}>✕</button>
      </td>
    </tr>
  );
}

const inputStyle = {
  width: '100%',
  background: 'var(--s2)',
  border: '1px solid var(--b2)',
  borderRadius: 6,
  padding: '4px 6px',
  fontSize: 12,
  color: 'var(--t1)',
  textAlign: 'right',
};

function BidDetail({ bid }) {
  const {
    bidLineItems, addBidLineItem, updateBidLineItem, deleteBidLineItem,
    updateBid, sendBid, generateBidPdf, custById, cloudMode, showToast,
  } = useApp();

  const items = useMemo(
    () => bidLineItems.filter(li => li.bidId === bid.id),
    [bidLineItems, bid.id]
  );

  const total = useMemo(
    () => items.reduce((a, li) => a + lineItemTotal(li), 0),
    [items]
  );

  const [generating, setGenerating] = useState(false);

  const handleSend = () => {
    if (items.length === 0) {
      if (!window.confirm('This bid has no line items. Send anyway?')) return;
    }
    // Persist computed total so the public portal + PDF can read it.
    updateBid(bid.id, { totalCents: Math.round(total * 100) });
    sendBid(bid.id);
  };

  const handleGeneratePdf = async () => {
    setGenerating(true);
    try {
      const res = await generateBidPdf(bid.id);
      if (res.ok && res.url) {
        window.open(res.url, '_blank', 'noopener');
        showToast('PDF generated ✓');
      } else {
        showToast(res.error || 'PDF generation failed', false);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyLink = () => {
    if (!bid.token) {
      showToast('Public link is created once the bid syncs to the cloud.', false);
      return;
    }
    const url = `${SITE_ORIGIN}/bid/${bid.token}`;
    try {
      navigator.clipboard.writeText(url);
      showToast('Public bid link copied');
    } catch {
      showToast(url);
    }
  };

  const cust = custById(bid.customerId);

  return (
    <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--b1)', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--t1)' }}>
              {bid.bidNumber} <span style={{ fontWeight: 400, color: 'var(--t3)', fontSize: 14 }}>{bid.projectName}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 4 }}>
              {cust?.name || '—'} · {bid.bidDate} → {bid.validUntil} · Estimator: {bid.estimator || '—'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button className="btn btn-ghost btn-sm" onClick={handleCopyLink} disabled={!bid.token}>Copy public link</button>
            <button className="btn btn-ghost btn-sm" onClick={handleGeneratePdf} disabled={generating || !cloudMode}>
              {generating ? 'Generating…' : 'Generate PDF'}
            </button>
            {bid.status === 'draft' && (
              <button className="btn btn-primary btn-sm" onClick={handleSend}>Send to customer</button>
            )}
            {bid.status === 'sent' && (
              <span className="bdg" style={{ background: 'var(--cyan-t)', color: 'var(--cyan)' }}>Sent — awaiting GC</span>
            )}
            {bid.status === 'approved' && (
              <span className="bdg" style={{ background: 'var(--teal-t)', color: 'var(--teal)' }}>✓ Approved</span>
            )}
            {bid.status === 'declined' && (
              <span className="bdg" style={{ background: 'var(--rose-t)', color: 'var(--rose)' }}>Declined</span>
            )}
          </div>
        </div>
        {!cloudMode && (
          <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--amber)', padding: '6px 10px', background: 'var(--amber-t)', borderRadius: 6 }}>
            Sign in to the cloud to generate a PDF or share a public bid link. Local-mode bids are draft-only.
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--t2)' }}>Line items</span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => addBidLineItem(bid.id, { sortOrder: items.length })}
          >
            + Add line item
          </button>
        </div>

        {items.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--t3)', fontSize: 13, border: '1px dashed var(--b2)', borderRadius: 8 }}>
            No line items yet. Click <strong>+ Add line item</strong> to start estimating.
          </div>
        ) : (
          <table className="tbl" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th>Type</th>
                <th>Description</th>
                <th>Unit</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th style={{ textAlign: 'right' }}>Labor hrs/u</th>
                <th style={{ textAlign: 'right' }}>Mat $/u</th>
                <th style={{ textAlign: 'right' }}>Mkup %</th>
                <th style={{ textAlign: 'right' }}>Subtotal</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <BidLineItemRow
                  key={item.id}
                  item={item}
                  onChange={(patch) => updateBidLineItem(item.id, patch)}
                  onDelete={() => deleteBidLineItem(item.id)}
                />
              ))}
            </tbody>
          </table>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--b1)' }}>
          <span style={{ fontSize: 13, color: 'var(--t2)' }}>Bid total ({items.length} line{items.length === 1 ? '' : 's'}, labor @ ${LABOR_RATE_PER_HR}/hr):</span>
          <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--t1)' }}>{fmtMoney(total)}</span>
        </div>
      </div>
    </div>
  );
}

export default function Bids() {
  const { bids, custById } = useApp();
  const [search, setSearch]         = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [showAddBid, setShowAddBid] = useState(false);

  const filtered = useMemo(
    () => bids.filter(b => {
      const cust = custById(b.customerId);
      return !search
        || (b.bidNumber  || '').toLowerCase().includes(search.toLowerCase())
        || (b.projectName|| '').toLowerCase().includes(search.toLowerCase())
        || (cust?.name   || '').toLowerCase().includes(search.toLowerCase());
    }),
    [bids, search, custById]
  );

  const selectedBid = bids.find(b => b.id === selectedId);

  const totals = useMemo(() => ({
    total:    bids.length,
    draft:    bids.filter(b => b.status === 'draft').length,
    sent:     bids.filter(b => b.status === 'sent').length,
    approved: bids.filter(b => b.status === 'approved').length,
  }), [bids]);

  return (
    <div className="page fade-up">
      <div className="ph">
        <div>
          <div className="ptitle">Bids</div>
          <div className="psub">{totals.sent} sent · {totals.approved} approved · {totals.draft} drafts</div>
        </div>
        <div className="pacts">
          <button className="btn btn-primary" onClick={() => setShowAddBid(true)}>+ New Bid</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, height: 'calc(100vh - 180px)', overflow: 'hidden' }}>
        <div className="card" style={{ width: 360, flexShrink: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--b1)' }}>
            <div className="search-bar">
              <span>🔍</span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search bids…" />
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>
                No bids yet — create your first bid to start estimating.
              </div>
            ) : filtered.map(bid => {
              const cust = custById(bid.customerId);
              const isSel = bid.id === selectedId;
              return (
                <div
                  key={bid.id}
                  onClick={() => setSelectedId(bid.id)}
                  style={{
                    padding: '11px 14px',
                    borderBottom: '1px solid var(--b1)',
                    cursor: 'pointer',
                    background: isSel ? 'var(--cyan-t)' : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)' }}>{bid.bidNumber}</div>
                    <span className="bdg" style={statusStyle(bid.status)}>{statusLabel(bid.status)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{bid.projectName}</div>
                  <div style={{ fontSize: 11, color: 'var(--t4)', marginTop: 2 }}>{cust?.name || '—'}</div>
                </div>
              );
            })}
          </div>
        </div>

        {selectedBid ? (
          <BidDetail bid={selectedBid} key={selectedBid.id} />
        ) : (
          <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <div style={{ textAlign: 'center', color: 'var(--t3)' }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Select a bid to edit line items</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Line items, totals, PDF, and the public approval link appear here.</div>
            </div>
          </div>
        )}
      </div>

      {showAddBid && <AddBidModal onClose={() => setShowAddBid(false)} />}
    </div>
  );
}

function statusStyle(s) {
  const map = {
    draft:    { background: 'var(--s3)',     color: 'var(--t3)' },
    sent:     { background: 'var(--cyan-t)', color: 'var(--cyan)' },
    approved: { background: 'var(--teal-t)', color: 'var(--teal)' },
    declined: { background: 'var(--rose-t)', color: 'var(--rose)' },
    expired:  { background: 'var(--amber-t)', color: 'var(--amber)' },
  };
  return map[s] || map.draft;
}
function statusLabel(s) {
  return (s || 'draft').charAt(0).toUpperCase() + (s || 'draft').slice(1);
}
